import React, {
  forwardRef,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  FC,
} from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { ChevronDown, Check, Search } from "lucide-react";
import { cx } from "class-variance-authority";
import classes from "./phoneInput.module.css";
import { cn } from "@/lib/utils";

export interface PhoneInputProps {
  label?: string;
  labelClassName?: string;
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (country: string | undefined) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  variant?: "light" | "dark";
}

const DEFAULT_COUNTRY: RPNInput.Country = "LT";

const PhoneInput: FC<PhoneInputProps> = ({
  label,
  labelClassName,
  value,
  onChange,
  onCountryChange,
  onBlur,
  error,
  id,
  name,
  disabled,
  className,
  wrapperClassName,
  variant = "light",
}: PhoneInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [country, setCountry] = useState<RPNInput.Country>(() => {
    if (!value) return DEFAULT_COUNTRY;
    try {
      const parsed = RPNInput.parsePhoneNumber(value);
      return parsed?.country ?? DEFAULT_COUNTRY;
    } catch {
      return DEFAULT_COUNTRY;
    }
  });

  const handleCountryChange = useCallback(
    (newCountry: RPNInput.Country | undefined) => {
      const next = newCountry ?? DEFAULT_COUNTRY;
      setCountry(next);
      onCountryChange?.(next);
    },
    [onCountryChange],
  );

  return (
    <div className={cx(classes.inputGroup, className)}>
      {label && <span className={cx("pl-5", labelClassName)}>{label}</span>}
      <div
        className={cn(
          classes.phoneWrapper,
          "h-[58px] rounded-[30px] border",
          variant === "light"
            ? "border-line-strong text-ink bg-white"
            : "border-dark text-ink",
          disabled && "bg-surface text-muted cursor-not-allowed",
          isFocused && classes.phoneWrapperFocused,
          disabled && classes.phoneWrapperDisabled,
          error && classes.phoneWrapperError,
          wrapperClassName,
        )}
      >
        <RPNInput.default
          className="flex w-full items-center overflow-visible"
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelectDropdown}
          inputComponent={PhoneNumberInput}
          international={false}
          smartCaret={false}
          defaultCountry={DEFAULT_COUNTRY}
          country={country}
          value={value || undefined}
          onCountryChange={handleCountryChange}
          onChange={(val) => onChange(val || "")}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e as React.FocusEvent<HTMLInputElement>);
          }}
          id={id}
          name={name}
          disabled={disabled}
        />
      </div>
      {error && <span className={classes.errorText}>{error}</span>}
    </div>
  );
};

export default PhoneInput;

// ─── Inner phone number input ────────────────────────────────────────────────

const PhoneNumberInput = forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => (
  <input
    className={cx(classes.phoneInput, className)}
    inputMode="tel"
    {...props}
    ref={ref}
  />
));
PhoneNumberInput.displayName = "PhoneNumberInput";

// ─── Country select dropdown ─────────────────────────────────────────────────

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: Array<CountryEntry>;
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelectDropdown = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => searchRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const filteredCountries = useMemo(() => {
    const valid = countryList.filter((c) => c.value);
    if (!search.trim()) return valid;
    const q = search.toLowerCase();
    return valid.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        RPNInput.getCountryCallingCode(c.value!).includes(q),
    );
  }, [countryList, search]);

  const callingCode = selectedCountry
    ? `+${RPNInput.getCountryCallingCode(selectedCountry)}`
    : "";

  return (
    <div ref={containerRef} className={classes.countryWrapper}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={cx(
          classes.countryButton,
          disabled && classes.countryButtonDisabled,
        )}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <FlagComponent
          country={selectedCountry}
          countryName={selectedCountry}
        />
        <span className={classes.callingCode}>{callingCode}</span>
        <ChevronDown
          size={14}
          className={cx(classes.chevron, isOpen && classes.chevronOpen)}
        />
      </button>

      <div className={classes.separator} />

      {isOpen && (
        <div className={classes.dropdown} role="dialog">
          <div className={classes.searchRow}>
            <Search size={14} className={classes.searchIcon} />
            <input
              ref={searchRef}
              type="text"
              className={classes.searchInput}
              placeholder="Ieškoti šalies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className={classes.dropdownList} role="listbox">
            {filteredCountries.map(({ value, label }) =>
              value ? (
                <li
                  key={value}
                  role="option"
                  aria-selected={value === selectedCountry}
                  className={cx(
                    classes.dropdownItem,
                    value === selectedCountry && classes.dropdownItemActive,
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(value);
                    setIsOpen(false);
                    setSearch("");
                  }}
                >
                  <FlagComponent country={value} countryName={label} />
                  <span className={classes.countryName}>{label}</span>
                  <span className={classes.countryCallingCode}>
                    +{RPNInput.getCountryCallingCode(value)}
                  </span>
                  {value === selectedCountry && (
                    <Check size={13} className={classes.checkIcon} />
                  )}
                </li>
              ) : null,
            )}
            {filteredCountries.length === 0 && (
              <li className={classes.noResults}>Šalies nerasta</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Flag ────────────────────────────────────────────────────────────────────

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];
  return (
    <span className={classes.flag} aria-label={countryName}>
      {Flag ? <Flag title={countryName} /> : null}
    </span>
  );
};
